import React from 'react';
import {
  FileText, Download, Eye, CheckCircle, Loader2,
  Calendar, Building2, Package, TrendingUp,
} from 'lucide-react';

const Step3ContractFinalize = ({
  selectedLaboratory,
  selectedProducts,
  promotions,
  contractData,
  generatedPdfBase64,
  isGeneratingPdf,
  isCreating,
  onContractDataChange,
  onGeneratePdf,
  onCreateContract,
  onDownloadPdf,
  onPreviewPdf,
}) => {
  const totalSellerCommission = promotions.reduce((sum, promo, i) => {
    const product = selectedProducts[i];
    if (!product) return sum;
    if (promo.commissionType === 'PERCENTAGE') {
      return sum + product.price * ((promo.commissionAmount || 0) / 100);
    }
    return sum + (promo.commissionAmount || 0);
  }, 0);

  const isFormValid =
    contractData.nom &&
    contractData.number &&
    contractData.dateStart &&
    contractData.dateEnd;

  const fmtDate = str => {
    if (!str) return '—';
    return new Date(str).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  };

  return (
    <div>
      <div className="mb-5">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Finalisation du contrat</h2>
        <p className="text-sm text-gray-500">
          Vérifiez les informations, générez le PDF puis enregistrez
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* ── Left: Form (3/5) ── */}
        <div className="lg:col-span-3 space-y-4">

          {/* Nom */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Nom du contrat
            </label>
            <input
              type="text"
              value={contractData.nom}
              onChange={e => onContractDataChange('nom', e.target.value)}
              placeholder="Contrat de partenariat - Laboratoire"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Numéro */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Numéro de référence
            </label>
            <input
              type="text"
              value={contractData.number}
              onChange={e => onContractDataChange('number', e.target.value)}
              placeholder="MAZID-202501-XXXX"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Dates — auto-computed from promotions (read-only) */}
          {contractData.dateStart && contractData.dateEnd && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Date de début
                </label>
                <div className="w-full px-3 py-2.5 border border-gray-100 bg-gray-50 rounded-xl text-sm text-gray-700 flex items-center gap-2">
                  <Calendar size={14} className="text-gray-400 flex-shrink-0" />
                  {fmtDate(contractData.dateStart)}
                </div>
                <p className="text-xs text-gray-400 mt-1">Déduite de la 1ère promotion</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Date de fin
                </label>
                <div className="w-full px-3 py-2.5 border border-gray-100 bg-gray-50 rounded-xl text-sm text-gray-700 flex items-center gap-2">
                  <Calendar size={14} className="text-gray-400 flex-shrink-0" />
                  {fmtDate(contractData.dateEnd)}
                </div>
                <p className="text-xs text-gray-400 mt-1">Déduite de la dernière promotion</p>
              </div>
            </div>
          )}

          {/* PDF actions */}
          <div className="pt-1">
            {!generatedPdfBase64 ? (
              <button
                onClick={onGeneratePdf}
                disabled={!isFormValid || isGeneratingPdf}
                className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-gray-900 text-white rounded-xl text-sm font-medium hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                {isGeneratingPdf ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Génération du PDF...
                  </>
                ) : (
                  <>
                    <FileText size={16} />
                    Générer le PDF
                  </>
                )}
              </button>
            ) : (
              <div className="space-y-2">
                {/* Success banner */}
                <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
                  <CheckCircle size={15} className="flex-shrink-0" />
                  <span className="font-medium">PDF généré avec succès</span>
                  <div className="ml-auto flex gap-2">
                    {onPreviewPdf && (
                      <button
                        onClick={onPreviewPdf}
                        className="flex items-center gap-1 text-xs text-green-700 hover:text-green-900 font-medium underline underline-offset-2"
                      >
                        <Eye size={12} /> Aperçu
                      </button>
                    )}
                    <button
                      onClick={onDownloadPdf}
                      className="flex items-center gap-1 text-xs text-green-700 hover:text-green-900 font-medium underline underline-offset-2"
                    >
                      <Download size={12} /> Télécharger
                    </button>
                  </div>
                </div>

                {/* Regenerate */}
                <button
                  onClick={onGeneratePdf}
                  disabled={isGeneratingPdf}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-200 rounded-xl text-xs text-gray-500 hover:bg-gray-50 disabled:opacity-40"
                >
                  {isGeneratingPdf ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <FileText size={13} />
                  )}
                  Regénérer le PDF
                </button>

                {/* Create contract */}
                <button
                  onClick={onCreateContract}
                  disabled={isCreating}
                  className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                >
                  {isCreating ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={16} />
                      Créer le contrat
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── Right: Summary (2/5) ── */}
        <div className="lg:col-span-2">
          <div className="bg-gray-50 rounded-2xl p-5 space-y-5">
            <h3 className="text-sm font-semibold text-gray-700">Récapitulatif</h3>

            {/* Lab */}
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-white rounded-xl flex items-center justify-center shadow-sm flex-shrink-0">
                <Building2 size={15} className="text-gray-500" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-400">Laboratoire</p>
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {selectedLaboratory?.nom}
                </p>
              </div>
            </div>

            {/* Products */}
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 bg-white rounded-xl flex items-center justify-center shadow-sm flex-shrink-0">
                <Package size={15} className="text-gray-500" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-gray-400 mb-1">
                  {selectedProducts.length} produit{selectedProducts.length > 1 ? 's' : ''}
                </p>
                <div className="space-y-1">
                  {selectedProducts.map((product, i) => {
                    const promo = promotions[i] || {};
                    const isPercent = (promo.commissionType || 'PERCENTAGE') === 'PERCENTAGE';
                    const amount = isPercent
                      ? (product.price * (promo.commissionAmount || 0)) / 100
                      : promo.commissionAmount || 0;
                    return (
                      <div key={product.id} className="flex justify-between text-xs">
                        <span className="text-gray-500 truncate mr-2">{product.name}</span>
                        <span className="text-green-600 font-medium flex-shrink-0">
                          +{amount.toFixed(2)} DH
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Total commission */}
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-white rounded-xl flex items-center justify-center shadow-sm flex-shrink-0">
                <TrendingUp size={15} className="text-green-500" />
              </div>
              <div>
                <p className="text-xs text-gray-400">Commission vendeur / scan</p>
                <p className="text-sm font-bold text-green-600">
                  {totalSellerCommission.toFixed(2)} DH
                </p>
              </div>
            </div>

            {/* Period */}
            {contractData.dateStart && contractData.dateEnd && (
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-white rounded-xl flex items-center justify-center shadow-sm flex-shrink-0">
                  <Calendar size={15} className="text-gray-500" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">Période</p>
                  <p className="text-xs font-medium text-gray-900">
                    {fmtDate(contractData.dateStart)}
                  </p>
                  <p className="text-xs text-gray-400">→ {fmtDate(contractData.dateEnd)}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step3ContractFinalize;
